// @flow weak

import {combineReducers} from 'redux';
import {
  REQUEST, SUCCESS,
  FAILURE, FILTER,
  PRODUCT_TYPES_REQUEST, PRODUCT_TYPES_SUCCESS,
  PRODUCT_TYPES_FAILURE, SORT,
} from '../actions/productList';
import _ from 'lodash/fp';

const notArchived = _.filter({archived: null});

const error = (state = null, action) => {
  switch (action.type) {
    case REQUEST:
    case SUCCESS:
      return null;
    case FAILURE:
      return action.error;
    default:
      return state;
  }
};

const isFetching = (state = false, action) => {
  switch (action.type) {
    case REQUEST:
      return true;
    case SUCCESS:
    case FAILURE:
      return false;
    default:
      return state;
  }
};

const archived = (state = false, action) => {
  switch (action.type) {
    case FILTER:
      return action.archived;
    default:
      return state;
  }
};

const productType = (state = null, action) => {
  switch (action.type) {
    case FILTER:
      return action.productType;
    default:
      return state;
  }
};

const query = (state = null, action) => {
  switch (action.type) {
    case FILTER:
      return action.query;
    default:
      return state;
  }
};

const showVariants = (state = true, action) => {
  switch (action.type) {
    case FILTER:
      return !!action.showVariants;
    default:
      return state;
  }
};

const filter = combineReducers({
  archived,
  productType,
  query,
  showVariants,
});

const list = (state = [], action) => {
  switch (action.type) {
    case SUCCESS:
      return _.map(p => ({
        ...p,
        mainVariant: _.find({id: p.mainVariantId}, p.variants),
        hasRelated: _.some('hasRelated', p.variants),
      }), action.products);
    default:
      return state;
  }
};

const variants = (state = [], action) => {
  switch (action.type) {
    case SUCCESS:
      return _.flatMap(product =>
        _.map(v => ({
          ...v,
          name: `${product.name} - ${v.name}`,
          product,
        }), product.variants), action.products);
    default:
      return state;
  }
};

const productTypesList = (state = [], action) => {
  switch (action.type) {
    case PRODUCT_TYPES_SUCCESS:
      return notArchived(action.productTypes);
    case PRODUCT_TYPES_REQUEST:
      return [];
    default:
      return state;
  }
};

const productTypesIsFetching = (state = false, action) => {
  switch (action.type) {
    case PRODUCT_TYPES_REQUEST:
      return true;
    case PRODUCT_TYPES_SUCCESS:
    case PRODUCT_TYPES_FAILURE:
      return false;
    default:
      return state;
  }
};

const productTypesError = (state = null, action) => {
  switch (action.type) {
    case PRODUCT_TYPES_FAILURE:
      return action.error;
    case PRODUCT_TYPES_REQUEST:
    case PRODUCT_TYPES_SUCCESS:
      return null;
    default:
      return state;
  }
};

const productTypes = combineReducers({
  list: productTypesList,
  error: productTypesError,
  isFetching: productTypesIsFetching,
});

const filterVariantProductTypes = productType =>
  _.filter({product: {productTypeId: productType}});

const isVariantArchived = _.filter(v =>
  _.isString(v.archived) ||
  _.isString(v.product.archived)
);

const variantNotArchived =
  _.filter({archived: null, product: {archived: null}});

const isProductArchived =
  _.filter(p => _.isString(p.archived));

const filterProductTypes = productType =>
  _.filter({productTypeId: productType});

const variantColumns = [
  'itemNumber',
  'name',
  'product.productType.name',
];

const productColumns = [
  'name',
  'productType.name',
];

const filterVariantQuery = query =>
  v => _.some(c =>
    _.includes(
      query,
      _.toLower(_.get(c, v))
    ), variantColumns
  );

const filterProductQuery = query =>
  p => _.some(c =>
    _.includes(
      query,
      _.toLower(_.get(c, p))
    ), productColumns
  ) || _.some(filterVariantQuery(query), p.variants);

const filterQuery = ({query, showVariants}) => {
  query = _.toLower(query);
  return _.isEmpty(query)
    ? _.identity
    : _.filter(showVariants
      ? filterVariantQuery(query)
      : filterProductQuery(query)
    );
};

const filteredList = ({
  filter,
  list,
  filteredList: filtered,
  variants,
  sorter,
} = {}, action) => {
  switch (action.type) {
    case SUCCESS:
    case FILTER:
    case SORT:
      let filterFunc = filter.archived
        ? filter.showVariants ? isVariantArchived : isProductArchived
        : filter.showVariants ? variantNotArchived : notArchived;

      if (!!filter.productType) {
        filterFunc = _.flow(
          filterFunc,
          filter.showVariants
            ? filterVariantProductTypes(filter.productType)
            : filterProductTypes(filter.productType)
        );
      }

      return _.flow(
        filterFunc,
        filterQuery(filter),
        sorter,
      )(filter.showVariants ? variants : list);
    default:
      return filtered;
  }
};

const sorter = (state = _.identity, action) => {
  switch (action.type) {
    case SORT:
      return action.sorter;
    default:
      return state;
  }
};

const reducers = combineReducers({
  error,
  filter,
  isFetching,
  list,
  filteredList: (state = []) => state,
  productTypes,
  variants,
  sorter,
});

const productList = (state, action) => {
  const newState = reducers(state, action);
  return {
    ...newState,
    filteredList: filteredList(newState, action),
  };
};

export default productList;



// WEBPACK FOOTER //
// ./src/reducers/productList.js